// Mirror-scene presentation (docs/ARCHITECTURE.md §8): client-only ESEngine systems that run every animation
// frame on the mirror scene. Sim positions arrive at SIM_HZ; InterpolationSystem renders every positioned entity
// INTERPOLATION_DELAY_SEC behind the newest view frame, between the two recorded samples around that time.
// ESEngine swallows system exceptions (Scene._runSystemPhase logs them), so every presentation system latches its
// first error and the Match rethrows it after the frame's Core.update (the sim's fault-latch rule, ARCHITECTURE §5).

import { Component, ECSComponent, EntitySystem, Matcher, type Entity } from '@esengine/ecs-framework'
import { SIM_HZ, secondsToTicks } from '@taxwar/net'

/** The 100 ms interpolation buffer between sim ticks. */
const INTERPOLATION_DELAY_SEC = 0.1
const INTERPOLATION_DELAY_TICKS = secondsToTicks(INTERPOLATION_DELAY_SEC)
/** Samples kept per entity: the delay window plus one sample on each side of it. */
const POSE_SAMPLES = INTERPOLATION_DELAY_TICKS + 2
/** Beyond this drift from its target (a hitch or a pause) the render clock jumps instead of easing. */
const CLOCK_SNAP_TICKS = 6
/** Fraction of the render clock's drift corrected per second. */
const CLOCK_CORRECTION_PER_SEC = 2

const HALF_TURN_DEG = 180
const FULL_TURN_DEG = 360
const FULL_TURN_RAD = Math.PI * 2

/** System update order within one mirror-scene frame. */
export const PresentationOrder = {
  interpolation: 0,
  entityViews: 10,
  lockLines: 20,
  attacks: 30,
} as const
export type PresentationOrder = (typeof PresentationOrder)[keyof typeof PresentationOrder]

/**
 * Client-only: recorded GeoPosition samples of one positioned mirror entity (oldest first) and its resolved
 * render pose. Attached by the mirror at spawn; never sent or decoded.
 */
@ECSComponent('PoseTrack')
export class PoseTrack extends Component {
  readonly ticks = new Float64Array(POSE_SAMPLES)
  readonly lats = new Float64Array(POSE_SAMPLES)
  readonly lons = new Float64Array(POSE_SAMPLES)
  readonly headings = new Float64Array(POSE_SAMPLES)
  count = 0
  /** Render pose: degrees, degrees, radians clockwise from north. */
  lat = 0
  lon = 0
  heading = 0
}

/** Appends the entity's synced position at `tick` (a repeat of the newest tick overwrites it). */
export function recordPose(track: PoseTrack, tick: number, lat: number, lon: number, heading: number): void {
  let slot = track.count
  if (slot > 0 && track.ticks[slot - 1] === tick) {
    slot--
  } else if (slot === POSE_SAMPLES) {
    track.ticks.copyWithin(0, 1)
    track.lats.copyWithin(0, 1)
    track.lons.copyWithin(0, 1)
    track.headings.copyWithin(0, 1)
    slot--
  } else {
    track.count++
  }
  track.ticks[slot] = tick
  track.lats[slot] = lat
  track.lons[slot] = lon
  track.headings[slot] = heading
  // A fresh track shows its first sample at once (before the next frame resolves it).
  if (track.count === 1) {
    track.lat = lat
    track.lon = lon
    track.heading = heading
  }
}

function wrapDegrees(deg: number): number {
  return deg - FULL_TURN_DEG * Math.floor((deg + HALF_TURN_DEG) / FULL_TURN_DEG)
}

function wrapRadians(rad: number): number {
  return rad - FULL_TURN_RAD * Math.floor((rad + Math.PI) / FULL_TURN_RAD)
}

/** Resolves the render pose at `renderTick`, clamped to the recorded window; longitude/heading take the short way. */
function resolvePose(track: PoseTrack, renderTick: number): void {
  const last = track.count - 1
  let from = 0
  while (from < last && (track.ticks[from + 1] as number) <= renderTick) from++
  const to = from < last ? from + 1 : from
  const t0 = track.ticks[from] as number
  const t1 = track.ticks[to] as number
  const t = t1 > t0 ? Math.min(1, Math.max(0, (renderTick - t0) / (t1 - t0))) : 0
  const lat0 = track.lats[from] as number
  const lon0 = track.lons[from] as number
  const heading0 = track.headings[from] as number
  track.lat = lat0 + ((track.lats[to] as number) - lat0) * t
  track.lon = wrapDegrees(lon0 + wrapDegrees((track.lons[to] as number) - lon0) * t)
  track.heading = heading0 + wrapRadians((track.headings[to] as number) - heading0) * t
}

/** Render time in (fractional) sim ticks, trailing the newest view frame by the interpolation delay. */
export class RenderClock {
  private latestTick = 0
  private received = false
  private current = 0

  get renderTick(): number {
    return this.current
  }

  /** A view frame for `tick` was applied. */
  onFrame(tick: number): void {
    this.latestTick = tick
    if (!this.received) {
      this.received = true
      this.current = tick - INTERPOLATION_DELAY_TICKS
    }
  }

  /** Advances with the frame delta, easing toward the target and never passing the newest frame. */
  advance(dtSeconds: number): void {
    if (!this.received) return
    const target = this.latestTick - INTERPOLATION_DELAY_TICKS
    let next = this.current + dtSeconds * SIM_HZ
    const drift = target - next
    if (Math.abs(drift) > CLOCK_SNAP_TICKS) next = target
    else next += drift * Math.min(1, CLOCK_CORRECTION_PER_SEC * dtSeconds)
    this.current = Math.min(next, this.latestTick)
  }
}

/** A mirror-scene system that latches its first error for the Match to rethrow (ESEngine only logs it). */
export abstract class PresentationSystem extends EntitySystem {
  private fault: { readonly error: unknown } | null = null

  protected constructor(matcher: Matcher, order: PresentationOrder) {
    super(matcher)
    this.setUpdateOrder(order)
  }

  /** The first error this system threw, if any. */
  get latchedFault(): { readonly error: unknown } | null {
    return this.fault
  }

  protected override process(entities: readonly Entity[]): void {
    if (this.fault !== null) return
    try {
      this.present(entities)
    } catch (error) {
      this.fault = { error }
      throw error
    }
  }

  protected abstract present(entities: readonly Entity[]): void
}

/** Resolves every tracked entity's render pose for this frame (runs first). */
export class InterpolationSystem extends PresentationSystem {
  constructor(private readonly clock: RenderClock) {
    super(Matcher.all(PoseTrack), PresentationOrder.interpolation)
  }

  protected override present(entities: readonly Entity[]): void {
    const renderTick = this.clock.renderTick
    for (let i = 0; i < entities.length; i++) {
      const track = (entities[i] as Entity).getComponent(PoseTrack) as PoseTrack
      resolvePose(track, renderTick)
    }
  }
}
